import type { FormInstance } from 'antd'
import { Alert, Flex } from 'antd'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { parseApiErrors, type ShowApiErrorsOptions } from './showApiErrors'

/**
 * Inline counterpart to {@link useShowApiErrors}: instead of a corner
 * notification toast, page-level API errors render as an `<Alert>` next to the
 * form, where the user is actually looking. Field-specific errors still route
 * to the matching form field via `setFields`.
 *
 * Use on auth/login screens (and anywhere a full-page form benefits from a
 * contextual error). For in-app pages where a transient toast is appropriate,
 * keep {@link useShowApiErrors}.
 *
 * ```tsx
 * const { showApiErrors, errorAlert } = useInlineApiErrors()
 * // ...in onError: showApiErrors(error, form)
 * <Flex vertical gap={24}>
 *   <AuthHeader ... />
 *   {errorAlert}
 *   <Form ...>...</Form>
 * </Flex>
 * ```
 */
export const useInlineApiErrors = () => {
  const [messages, setMessages] = useState<string[]>([])
  const { t } = useTranslation()

  const clearErrors = useCallback(() => setMessages([]), [])

  /** Show pre-resolved messages directly (e.g. seeded from a redirect). */
  const showErrorMessages = useCallback((next: string[]) => {
    setMessages([...new Set(next)])
  }, [])

  const showApiErrors = useCallback(
    (error: unknown, form?: FormInstance, options?: ShowApiErrorsOptions) => {
      const {
        fieldErrors,
        messages: parsed,
        unexpected
      } = parseApiErrors(error, form, options)

      if (form && fieldErrors.length) {
        form.setFields(
          fieldErrors.map(({ name, errors }) => ({ name, errors }))
        )
      }

      // Replace (not append) so a fresh attempt supersedes the previous error,
      // and a now field-only error clears any stale page-level alert.
      setMessages(
        unexpected
          ? [options?.fallbackMessage ?? t('errors.somethingWentWrong')]
          : [...new Set(parsed)]
      )
    },
    [t]
  )

  const errorAlert = useMemo(
    () =>
      messages.length ? (
        <Alert
          type="error"
          showIcon
          closable={{ onClose: clearErrors }}
          title={
            messages.length === 1 ? (
              messages[0]
            ) : (
              <Flex vertical gap={2}>
                {messages.map((msg, index) => (
                  <span key={`${index}-${msg}`}>{msg}</span>
                ))}
              </Flex>
            )
          }
        />
      ) : null,
    [messages, clearErrors]
  )

  return { showApiErrors, errorAlert, showErrorMessages, clearErrors }
}
